/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\DirectRouteGeneralSection.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the PBX Applications module.
----------------------------------------------------------------*/


import { MapPin } from 'lucide-react';
import { FormBox } from '../../../components/ui/FormBox';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import Toggle from '../../../components/ui/Toggle';
import { SimpleTooltip } from '../../../components/ui/SimpleTooltip';
import SelectClassOfService from '../../../components/ui/SelectClassOfService';
import { CallerIdInput } from '../../../components/ui/CallerIdInput';

interface ClassOfService {
    id: number;
    name: string;
    description?: string;
}

interface DirectRouteFormData {
    name: string;
    description: string;
    context: string;
    numberToDial: string;
    classOfServicesId: string | number;
    callerIdName: string;
    callerIdNumber: string;
    enabled: boolean;
}

interface DirectRouteGeneralSectionProps {
    formData: DirectRouteFormData;
    errors: Record<string, string>;
    classOfServices: ClassOfService[];
    onFieldChange: <K extends keyof DirectRouteFormData>(field: K, value: DirectRouteFormData[K]) => void;
    t: (key: string) => string;
}

export function DirectRouteGeneralSection({
    formData, errors, classOfServices, onFieldChange, t,
}: DirectRouteGeneralSectionProps) {
    return (
        <FormBox title={t('directRoute.basicInformation')} icon={<MapPin className="h-5 w-5" />}>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-center">

                {/* Name */}
                <Label htmlFor="name">
                    <SimpleTooltip content={t('directRoute.fields.name.tooltip')}>
                        <span>
                            {t('directRoute.fields.name.label')}
                            <span className="text-red-500 ml-1">*</span>
                        </span>
                    </SimpleTooltip>
                </Label>
                <div>
                    <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => onFieldChange('name', e.target.value)}
                        placeholder={t('directRoute.fields.name.placeholder')}
                        className={errors.name ? 'border-red-500' : ''}
                    />
                    {errors.name && <p className="text-sm text-red-500 mt-1">{errors.name}</p>}
                </div>

                {/* Caller ID */}
                <Label htmlFor="callerId">
                    <SimpleTooltip content={t('directRoute.fields.callerIdName.tooltip')}>
                        <span>{t('directRoute.fields.callerIdName.label')}</span>
                    </SimpleTooltip>
                </Label>
                <div>
                    <CallerIdInput
                        id="callerId"
                        nameValue={formData.callerIdName || ''}
                        numberValue={formData.callerIdNumber || ''}
                        onNameChange={(value) => onFieldChange('callerIdName', value)}
                        onNumberChange={(value) => onFieldChange('callerIdNumber', value)}
                        namePlaceholder={t('directRoute.fields.callerIdName.placeholder')}
                        numberPlaceholder={t('directRoute.fields.callerIdNumber.placeholder')}
                        nameTooltip={t('directRoute.fields.callerIdName.tooltip')}
                        numberTooltip={t('directRoute.fields.callerIdNumber.tooltip')}
                    />
                </div>

                {/* Number to Dial */}
                <Label htmlFor="numberToDial">
                    <SimpleTooltip content={t('directRoute.fields.numberToDial.tooltip')}>
                        <span>
                            {t('directRoute.fields.numberToDial.label')}
                            <span className="text-red-500 ml-1">*</span>
                        </span>
                    </SimpleTooltip>
                </Label>
                <div>
                    <Input
                        id="numberToDial"
                        value={formData.numberToDial}
                        onChange={(e) => onFieldChange('numberToDial', e.target.value)}
                        placeholder={t('directRoute.fields.numberToDial.placeholder')}
                        className={errors.numberToDial ? 'border-red-500' : ''}
                    />
                    {errors.numberToDial && <p className="text-sm text-red-500 mt-1">{errors.numberToDial}</p>}
                </div>

                {/* Class of Service */}
                <Label htmlFor="classOfServicesId">
                    <SimpleTooltip content={t('directRoute.fields.classOfServices.tooltip')}>
                        <span>
                            {t('directRoute.fields.classOfServices.label')}
                            <span className="text-red-500 ml-1">*</span>
                        </span>
                    </SimpleTooltip>
                </Label>
                <div>
                    <SelectClassOfService
                        value={formData.classOfServicesId || null}
                        onChange={(value) => onFieldChange('classOfServicesId', value || '')}
                        classOfServices={classOfServices}
                        placeholder={t('directRoute.fields.classOfServices.placeholder')}
                        error={!!errors.classOfServicesId}
                    />
                    {errors.classOfServicesId && <p className="text-sm text-red-500 mt-1">{errors.classOfServicesId}</p>}
                </div>

                {/* Enabled */}
                <Label>
                    <SimpleTooltip content={t('directRoute.fields.enabled.tooltip')}>
                        <span>{t('directRoute.fields.enabled.label')}</span>
                    </SimpleTooltip>
                </Label>
                <div>
                    <Toggle
                        value={formData.enabled}
                        onChange={(checked) => onFieldChange('enabled', checked)}
                    />
                </div>
                <div className="col-span-2"></div>

            </div>
        </FormBox>
    );
}
